import * as React from "react";
import * as _ from "lodash";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import {
  SubmitHandler,
  useForm
} from "react-hook-form";
import { globalEmailRegex } from "@gravitywelluk/regex";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";

import { Text } from "src/library/components/Text";
import {
  CheckboxLabel,
  CheckboxStyled,
  FormRow,
  Input
} from "src/library/components/Forms";
import { Button } from "src/library/components/Button";
import type { IStore } from "src/core/shared/store";
import {
  UserSettingName,
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { setGlobalUser } from "src/core/shared/store/global/global.actions";

interface UserSettingsFormProps {
  name: string;
  email: string;
  emailNotifications: boolean;
  platformNotifications: boolean;
  errorNotifications: boolean;
}

/**
 * User settings tab
 */
const UserSettingsTab: React.ReactFCWithChildren = () => {
  const {
    register, setValue, errors, handleSubmit
  } = useForm<UserSettingsFormProps>();

  const dispatch = useDispatch();
  const [ loading, setLoading ] = React.useState(false);
  const [ emailNotificationsState, setEmailNotificationsState ] = React.useState(false);
  const [ platformNotificationsState, setPlatformNotificationsState ] = React.useState(false);
  const [ errorNotificationsState, setErrorNotificationsState ] = React.useState(false);
  const [ updateUserRes, updateUserReq ] = useUserApi("UPDATE_USER");
  const { currentUser } = useMappedState(({ global }: IStore) => ({ currentUser: global.user as IUser }));

  // Compile the current user settings
  const currentUserSettings = React.useMemo((): UserSettingName[] | never[] => {
    const userSettings: UserSettingName[] = [];

    // If there are no userSettings, return an empty array
    if (!currentUser.userSettings) {
      return [];
    }

    // Loop through each of the currentUser.userSettings and find which settings
    // are enabled
    for (const i in currentUser.userSettings) {
      const settings = currentUser.userSettings[ i ];

      // If the settings are enabled, add them to our user
      if (settings.enabled) {
        userSettings.push(settings.settingName);
      }
    }

    return userSettings;
  }, [ currentUser ]);

  /**
   * Handle form submission
   */
  const handleOnFormSubmit: SubmitHandler<UserSettingsFormProps> = React.useCallback(
    ({
      name, emailNotifications, platformNotifications, errorNotifications
    }) => {
      const nameSplit = name.split(" ");
      const firstName = nameSplit.shift();
      const lastName = nameSplit.join(" ");
      const userSettings: UserSettingName[] = [];

      // Error if no name or last name is given
      if (name.length <= 1 || !firstName || lastName.length === 0) {
        toast.error("Please provide a first name and last name.");

        return;
      }

      // If emailNotifications exists, at it to the userSettings array
      if (emailNotifications) {
        userSettings.push(UserSettingName.EmailNotifications);
      }

      // If platformNotifications exists, at it to the userSettings array
      if (platformNotifications) {
        userSettings.push(UserSettingName.PlatformNotifications);
      }

      // If errorNotifications exists, at it to the userSettings array
      if (errorNotifications) {
        userSettings.push(UserSettingName.ErrorNotifications);
      }

      // Update the loading state
      setLoading(true);

      // Update the user
      updateUserReq({
        id: currentUser.id,
        firstName,
        lastName,
        userSettings
      });
    },
    [ currentUser.id, updateUserReq ]
  );

  // On mount
  React.useEffect(() => {
    // Update the emailNotificationsState
    setEmailNotificationsState(_.includes(currentUserSettings, UserSettingName.EmailNotifications));
    // Update the platformNotificationsState
    setPlatformNotificationsState(_.includes(currentUserSettings, UserSettingName.PlatformNotifications));
    // Update the rrrorNotificationsState
    setErrorNotificationsState(_.includes(currentUserSettings, UserSettingName.ErrorNotifications));
  }, [ currentUserSettings ]);

  // On updateUserRes change
  React.useEffect(() => {
    // If an error occurs
    if (updateUserRes.error) {
      // Update the loading state
      setLoading(false);
      // Inform the user of the error
      toast.error(updateUserRes.error.message);
    }

    // On success
    if (updateUserRes.data) {
      // Update the current user store
      dispatch(setGlobalUser(updateUserRes.data, true));
      // Update the loading state
      setLoading(false);
      // Inform the user of the success
      toast.success("Your user settings have updated successfully.");
    }
  }, [
    updateUserRes.error,
    updateUserRes.data,
    dispatch
  ]);

  return (
    <form onSubmit={handleSubmit(handleOnFormSubmit)}>
      <FormFieldset>
        <Text as="h2">
          User Details
        </Text>

        <FormRow>
          <Input
            as="input"
            ref={register({ required: "Please provide your full name" })}
            name="name"
            label="Name"
            defaultValue={`${currentUser.firstName} ${currentUser.lastName}`}
            error={errors.name ? errors.name.message : undefined}
            handleChange={event => setValue("name", event.target.value)}
          />
        </FormRow>

        <FormRow>
          <Input
            as="input"
            disabled
            ref={register({
              required: "Please provide your email address",
              pattern: globalEmailRegex
            })}
            name="email"
            label="Email"
            defaultValue={currentUser.email}
            error={errors.email ? errors.email.message : undefined}
            handleChange={event => setValue("email", event.target.value)}
          />
        </FormRow>
      </FormFieldset>

      <FormFieldset>
        <Text as="h2">
          Notification settings
        </Text>

        <CheckboxLabel
          for="emailNotifications"
          label="Email notifications - get platform updates by email"
        >
          <CheckboxStyled
            ref={register()}
            id="emailNotifications"
            name="emailNotifications"
            checked={emailNotificationsState}
            onChange={event => {
              // Update the emailNotificationsState
              setEmailNotificationsState(event.target.checked);
              // Update the react-hook-form value
              setValue("emailNotifications", event.target.checked);
            }}
          />
        </CheckboxLabel>

        <CheckboxLabel
          for="platformNotifications"
          label="In app notifications - get platform updates by in app notifications"
        >
          <CheckboxStyled
            ref={register()}
            id="platformNotifications"
            name="platformNotifications"
            checked={platformNotificationsState}
            onChange={event => {
              // Update the platformNotificationsState
              setPlatformNotificationsState(event.target.checked);
              // Update the react-hook-form value
              setValue("platformNotifications", event.target.checked);
            }}
          />
        </CheckboxLabel>

        <CheckboxLabel
          for="errorNotifications"
          label="Error notifications - enable or disable notifications specifically relating to errors"
        >
          <CheckboxStyled
            ref={register()}
            id="errorNotifications"
            name="errorNotifications"
            checked={errorNotificationsState}
            onChange={event => {
              // Update the errorNotificationsState
              setErrorNotificationsState(event.target.checked);
              // Update the react-hook-form value
              setValue("errorNotifications", event.target.checked);
            }}
          />
        </CheckboxLabel>

        <FormRow>
          <Button
            loading={loading}
            type="submit"
          >
            Save
          </Button>
        </FormRow>
      </FormFieldset>
    </form>
  );
};

export default UserSettingsTab;

const FormFieldset = styled.fieldset`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.lg};
    background: ${theme.colors.white};
  `}
`;
